import { useState } from 'react';
import { fmtMoney, fmtMoneyShort, fmtNumber } from '@/lib/format';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Mail, Phone, MessageCircle } from 'lucide-react';

const LEVEL_COLOR = {
  1: '#c9a84c',  // oro — directo
  2: '#8b5cf6',  // violeta — nivel 2
  3: '#10b981',  // verde — nivel 3
  4: '#3b82f6',  // azul — nivel 4+
};

function LevelPill({ level }) {
  if (!level) return null;
  const color = LEVEL_COLOR[level] || LEVEL_COLOR[4];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0"
      style={{ color, borderColor: `${color}40`, backgroundColor: `${color}12` }}
    >
      Nivel {level}
    </span>
  );
}

// Datos de contacto de la red (decisión de Samuel, 09/10/2026): el broker Premium ve email y teléfono
// de su red hasta 3 niveles, solo para asistirla. El servidor solo los envía a brokers Premium y al equipo.
const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 8 ? `https://wa.me/${digits}` : null;
};

const chip = 'inline-flex items-center gap-1.5 min-h-[40px] sm:min-h-[28px] max-w-full px-3 sm:px-2.5 rounded-lg border border-white/[0.08] bg-white/[0.03] text-xs text-gray-300 hover:text-white hover:border-[#c9a84c]/40 transition-colors';

export function ContactLinks({ email, phone, name }) {
  if (!email && !phone) return null;
  const wa = waLink(phone);
  return (
    <div className="flex flex-wrap gap-2" data-testid="network-contact">
      {email && (
        <a href={`mailto:${email}`} className={chip} aria-label={`Escribir un email a ${name || email}`}>
          <Mail size={13} className="text-[#c9a84c] shrink-0" aria-hidden="true" />
          <span className="truncate">{email}</span>
        </a>
      )}
      {phone && (
        <a href={`tel:${String(phone).replace(/[^\d+]/g, '')}`} className={chip} aria-label={`Llamar a ${name || phone}`}>
          <Phone size={13} className="text-[#c9a84c] shrink-0" aria-hidden="true" />
          <span className="whitespace-nowrap">{phone}</span>
        </a>
      )}
      {wa && (
        <a href={wa} target="_blank" rel="noopener noreferrer" className={chip} aria-label={`Abrir WhatsApp con ${name || phone}`}>
          <MessageCircle size={13} className="text-[#25D366] shrink-0" aria-hidden="true" />
          <span>WhatsApp</span>
        </a>
      )}
    </div>
  );
}

function countDescendants(node) {
  if (!node?.children?.length) return 0;
  return node.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

/**
 * Nodo recursivo del árbol de red (accordion drill-down).
 * Todos los nodos arrancan colapsados. Pulsar la fila completa expande/colapsa.
 *
 * @param node             {id, name, email, phone, level, children, sales, commission, affiliate_status}
 * @param depth            profundidad visual (indentación)
 * @param showMetrics      si true, muestra ventas + comisión a la derecha
 * @param isRoot           si true, renderiza como "tú" sin +/- (no tiene sentido colapsarse a sí mismo)
 * @param currency         divisa de las comisiones (siempre euros)
 */
export function NetworkTreeNode({ node, depth = 0, showMetrics = true, isRoot = false, currency = 'eur' }) {
  const [open, setOpen] = useState(isRoot); // root abierto, descendientes colapsados
  const hasChildren = node?.children?.length > 0;
  const directCount = node?.children?.length || 0;
  const totalDescendants = hasChildren ? countDescendants(node) : 0;

  if (!node) return null;

  const expandable = hasChildren && !isRoot;
  // Fila desplegable accesible: botón con aria-expanded; el resto de filas, contenedor normal
  const Row = expandable ? 'button' : 'div';
  const rowProps = expandable
    ? { type: 'button', 'aria-expanded': open, onClick: () => setOpen(o => !o) }
    : {};

  return (
    <div className={depth > 0 ? 'border-l border-white/[0.06] pl-5 ml-3' : ''}>
      <Row
        {...rowProps}
        className={`w-full text-left flex items-center gap-2 py-2 px-2 rounded-lg transition-colors ${
          expandable ? 'cursor-pointer hover:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]/40' : ''
        }`}
      >
        {/* Toggle +/− (oculto en root) */}
        <span
          aria-hidden="true"
          className={`w-5 h-5 flex items-center justify-center rounded-md flex-shrink-0 transition-all ${
            hasChildren && !isRoot
              ? open
                ? 'bg-[#c9a84c]/15 text-[#c9a84c] border border-[#c9a84c]/30'
                : 'bg-white/[0.04] text-gray-400 border border-white/10 hover:border-[#c9a84c]/40 hover:text-[#c9a84c]'
              : 'opacity-0'
          }`}
        >
          {hasChildren && !isRoot && (open ? <Minus size={11} strokeWidth={2.5} /> : <Plus size={11} strokeWidth={2.5} />)}
        </span>

        {/* Avatar */}
        <span
          aria-hidden="true"
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{
            backgroundColor: isRoot ? '#c9a84c20' : '#ffffff08',
            color: isRoot ? '#c9a84c' : '#9ca3af',
          }}
        >
          {node.name?.charAt(0) || '?'}
        </span>

        {/* Info + badges */}
        <span className="flex items-center gap-3 min-w-0 flex-1">
          <span className="block min-w-0">
            <span className="block text-sm text-white font-medium truncate leading-tight">
              {node.name}
              {isRoot && <span className="text-[11px] text-[#c9a84c] ml-2 uppercase tracking-wider">(tú)</span>}
            </span>
            {node.subtitle && <span className="block text-[11px] text-gray-400 truncate">{node.subtitle}</span>}
          </span>

          {!isRoot && <LevelPill level={node.level} />}

          {hasChildren && (
            <span
              className="text-[11px] text-gray-400 bg-white/[0.03] border border-white/[0.06] px-2 py-0.5 rounded-full font-medium shrink-0"
              title={`${directCount} directo${directCount !== 1 ? 's' : ''} · ${totalDescendants} total en la red`}
            >
              {directCount} directo{directCount !== 1 ? 's' : ''}
              {totalDescendants > directCount && (
                <span className="text-gray-500 ml-1">· {totalDescendants} total</span>
              )}
            </span>
          )}

          {showMetrics && !isRoot && (
            <span className="flex items-center gap-3 shrink-0 ml-auto">
              <span className="text-[11px] text-gray-400 whitespace-nowrap">
                {node.kind === 'socio' && !node.sales ? (node.affiliate_status && node.affiliate_status !== 'Activo' ? node.affiliate_status : 'socio') : `${node.sales ?? 0} vta${node.sales !== 1 ? 's' : ''}`}
              </span>
              <span className="text-sm text-white font-medium whitespace-nowrap">
                {fmtMoney(node.commission ?? 0, currency)}
              </span>
            </span>
          )}
        </span>
      </Row>

      {/* Contacto: fuera del botón de la fila (no se anidan enlaces dentro de un botón) */}
      {!isRoot && (node.email || node.phone) && (
        <div className="pl-2 sm:pl-14 pr-2 pb-2">
          <ContactLinks email={node.email} phone={node.phone} name={node.name} />
        </div>
      )}

      <AnimatePresence initial={false}>
        {open && hasChildren && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            {node.children.map(child => (
              <NetworkTreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                showMetrics={showMetrics}
                currency={currency}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
